if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Back from checkout, the browser can restore this page with the button still mid-redirect.
        window.addEventListener('pageshow', (event) => {
          if (!event.persisted || !this.redirectingToCheckout) return;
          this.redirectingToCheckout = false;
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Mori Matcha only: the add-to-cart destination test (assets/mori-checkout-experiment.js).
        const checkoutArm =
          this.dataset.checkoutExperiment && !this.closest('quick-add-modal') && window.__moriCheckoutArm
            ? window.__moriCheckoutArm().catch(() => null)
            : Promise.resolve(null);
        // Mori Matcha only: works out the free gifts while the arm resolves, so they go in with the tin.
        const giftLines = this.prepareGiftLines();
        Promise.all([checkoutArm, giftLines]).then(([arm, gifts]) => this.addToCart(evt, arm === 'direct', gifts));
      }

      // The form's line as a /cart/add.js item, used when gifts are added alongside it.
      formLine(formData) {
        const line = { id: Number(formData.get('id')), quantity: parseInt(formData.get('quantity')) || 1 };
        if (formData.get('selling_plan')) line.selling_plan = Number(formData.get('selling_plan'));
        for (const [key, value] of formData.entries()) {
          const property = key.match(/^properties\[(.+)\]$/);
          if (property && typeof value === 'string') (line.properties ||= {})[property[1]] = value;
        }
        return line;
      }

      // Resolves the gift lines to add with this tin (snippets/mori-cart-gifts.liquid), or [] when
      // there are none or anything goes wrong; the gift sync after the add covers those cases.
      prepareGiftLines() {
        if (!window.__moriPrepareGiftAdd || this.closest('quick-add-modal')) return Promise.resolve([]);
        return window
          .__moriPrepareGiftAdd(this.formLine(new FormData(this.form)))
          .then((gifts) => gifts || [])
          .catch((error) => {
            console.error(error);
            return [];
          });
      }

      addToCart(evt, directCheckout, giftLines = [], retriedLinesUpdate = null) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const sections =
          this.cart && !directCheckout ? this.cart.getSectionsToRender().map((section) => section.id) : null;
        if (sections) this.cart.setActiveElement(document.activeElement);

        if (giftLines.length) {
          // One request for the tin and its gifts, so the drawer renders once with everything in it.
          const body = { items: [this.formLine(formData), ...giftLines] };
          if (sections) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
          }
          config.body = JSON.stringify(body);
        } else {
          delete config.headers['Content-Type'];
          if (sections) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = retriedLinesUpdate || this.createCartLinesUpdateEvent(variantId, quantity);

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // A gift that can't be added (sold out, say) mustn't block the tin: add it on its own
            // and let the gift sync sort out the rest.
            if (response.status && giftLines.length) {
              return this.addToCart(evt, directCheckout, [], linesUpdateDeferred);
            }
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (directCheckout) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              return this.redirectToCheckout();
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            if (this.redirectingToCheckout) return;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Waits for the free gift lines and the order attribution to reach the cart, so checkout
      // shows the gifts and the pixel can tie the order back to this visitor. The spinner stays on.
      async redirectToCheckout() {
        this.redirectingToCheckout = true;
        try {
          await window.__moriSyncGifts?.();
        } catch (error) {
          console.error(error);
        }
        try {
          await window.__moriPrepareDirectCheckout?.();
        } catch (error) {
          console.error(error);
        }
        window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
